/**
 * Theme: light / dark / system. `.dark` on <html> switches the token set in @prop/ui/index.css. The choice is kept per
 * browser; "system" follows prefers-color-scheme live.
 */
import { useEffect, useSyncExternalStore } from "react";

export type ThemeChoice = "light" | "dark" | "system";
export const THEMES: readonly ThemeChoice[] = ["light", "dark", "system"];
const STORAGE_KEY = "prop.theme";

function readChoice(): ThemeChoice {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {
    // storage blocked
  }
  return "system";
}

const media = () => window.matchMedia("(prefers-color-scheme: dark)");

export function applyTheme(choice: ThemeChoice): void {
  const dark = choice === "dark" || (choice === "system" && media().matches);
  document.documentElement.classList.toggle("dark", dark);
  document.documentElement.style.colorScheme = dark ? "dark" : "light";
}

/** Apply the saved theme before React renders (no flash of the wrong theme). */
export function bootTheme(): void {
  applyTheme(readChoice());
}

// One choice for the whole page (the header and the phone menu each show the switch), read once from storage.
let current: ThemeChoice | undefined;
const listeners = new Set<() => void>();
const snapshot = (): ThemeChoice => (current ??= readChoice());

function choose(choice: ThemeChoice): void {
  current = choice;
  applyTheme(choice);
  try {
    localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    // not persisted
  }
  for (const fn of listeners) fn();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * The theme choice. A prerendered page hydrates with "system" (the build's value, ADR-0061) and switches to the stored
 * choice right after; the page itself already has the right colours from bootTheme().
 */
export function useTheme(): [ThemeChoice, (t: ThemeChoice) => void] {
  const choice = useSyncExternalStore(subscribe, snapshot, () => "system" as const);
  useEffect(() => {
    if (choice !== "system") return;
    const m = media();
    const onChange = () => applyTheme("system");
    m.addEventListener("change", onChange);
    return () => m.removeEventListener("change", onChange);
  }, [choice]);
  return [choice, choose];
}
